import { Combobox } from "@/components/Combobox";
import { Input } from "@/components/ui/input";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useQueryChgManufacturers } from "@/lib/hooks/query/partner/useQueryChgManufacturers";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

interface ManufacturerFormValues {
  manufacturer: string;
  chgManufacturerId?: string;
}

interface ManufacturerFieldProps {
  isChgOrder: boolean;
  partnerId: number;
  label: string;
}

export function ManufacturerField({
  isChgOrder,
  partnerId,
  label,
}: Readonly<ManufacturerFieldProps>) {
  const { t } = useTranslation(undefined, {
    keyPrefix: "inbound.manufacturer-field",
  });
  const form = useFormContext<ManufacturerFormValues>();
  const manufacturersQuery = useQueryChgManufacturers(
    isChgOrder ? partnerId : undefined,
  );

  const options =
    manufacturersQuery.data?.map((manufacturer) => ({
      id: manufacturer.manufacturerId,
      label: manufacturer.name,
    })) ?? [];

  if (!isChgOrder) {
    return (
      <FormField
        control={form.control}
        name="manufacturer"
        render={({ field }) => (
          <FormItem className="w-full">
            <FormLabel>{label}</FormLabel>
            <FormControl>
              <Input {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    );
  }

  return (
    <FormField
      control={form.control}
      name="chgManufacturerId"
      render={({ field }) => (
        <FormItem className="w-full">
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Combobox
              value={field.value}
              options={options}
              onSelect={(value) => {
                field.onChange(value ?? "");
                form.setValue(
                  "manufacturer",
                  options.find((option) => option.id === value)?.label ?? "",
                  { shouldValidate: true },
                );
              }}
              trigger={{
                className: "w-full",
                placeholder: t("select-manufacturer"),
              }}
              command={{
                searchPlaceholder: t("search-manufacturer"),
                notFound: t("manufacturer-not-found"),
              }}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
